<script setup lang="ts">
interface BreadcrumbItem {
  name: string;
  path?: string;
}

const props = defineProps<{
  items: BreadcrumbItem[];
  /** Masque le fil visuel tout en gardant le JSON-LD BreadcrumbList (SEO).
   *  Utile sur les pages publiques où le breadcrumb alourdit le design. */
  hideVisual?: boolean;
}>();

// Schema.org BreadcrumbList pour le SEO
const schemaBreadcrumb = computed(() => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    {
      "@type": "ListItem",
      position: 1,
      name: "Accueil",
      item: "https://donoor.org",
    },
    ...props.items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 2,
      name: item.name,
      ...(item.path ? { item: `https://donoor.org${item.path}` } : {}),
    })),
  ],
}));

useHead({
  script: [
    {
      type: "application/ld+json",
      innerHTML: computed(() => JSON.stringify(schemaBreadcrumb.value)),
    },
  ],
});
</script>

<template>
  <!-- hideVisual : le JSON-LD (useHead ci-dessus) reste émis pour le SEO ;
       seul le fil visible est masqué. -->
  <nav v-if="!props.hideVisual" aria-label="Breadcrumb" class="py-4">
    <ol
      class="flex items-center gap-2 text-sm text-gray-500 flex-wrap"
      itemscope
      itemtype="https://schema.org/BreadcrumbList"
    >
      <!-- Accueil -->
      <li
        itemprop="itemListElement"
        itemscope
        itemtype="https://schema.org/ListItem"
      >
        <NuxtLink
          to="/"
          class="hover:text-main transition-colors flex items-center gap-1"
          itemprop="item"
        >
          <UIcon name="i-lucide-home" class="w-4 h-4" />
          <span itemprop="name">Accueil</span>
        </NuxtLink>
        <meta itemprop="position" content="1" />
      </li>

      <!-- Séparateur et items -->
      <template v-for="(item, index) in items" :key="index">
        <li class="text-gray-300">
          <UIcon name="i-lucide-chevron-right" class="w-4 h-4" />
        </li>
        <li
          itemprop="itemListElement"
          itemscope
          itemtype="https://schema.org/ListItem"
        >
          <NuxtLink
            v-if="item.path"
            :to="item.path"
            class="hover:text-main transition-colors"
            itemprop="item"
          >
            <span itemprop="name">{{ item.name }}</span>
          </NuxtLink>
          <span v-else class="text-carbon font-medium" itemprop="name">
            {{ item.name }}
          </span>
          <meta itemprop="position" :content="String(index + 2)" />
        </li>
      </template>
    </ol>
  </nav>
</template>
